import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import createSidebarSections from '../../config/sidebarSections';
import { environment } from '../../config/environment';
import Swal from 'sweetalert2';
import './CandidateList.css';

const CandidateList = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [filterPost, setFilterPost] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const [uniquePosts, setUniquePosts] = useState([]);
  const [uniqueGenders, setUniqueGenders] = useState([]);
  const [uniqueCategories, setUniqueCategories] = useState([]);
  // ✅ Edit Modal States (Add after isModalOpen)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);


  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sections = useMemo(
    () => createSidebarSections(navigate, null),
    [navigate]
  );

  const getGenderLabel = (genderId) => {
    const gender = uniqueGenders.find(g => g.id === genderId);
    if (gender?.label) return gender.label;

    const fallbackMap = {
      1: 'Male',
      2: 'Female',
      3: 'Other'
    };
    return fallbackMap[genderId] || 'N/A';
  };

  const getCategoryLabel = (categoryId) => {
    const category = uniqueCategories.find(c => c.id === categoryId);
    if (category?.label) return category.label;

    const fallbackMap = {
      1: 'General',
      2: 'OBC',
      3: 'SC',
      4: 'ST',
      5: 'EWS'
    };
    return fallbackMap[categoryId] || 'N/A';
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      0: 'Inactive',
      1: 'Active',
      2: 'Under Review',
      3: 'Shortlisted',
      4: 'Rejected'
    };
    return statusMap[status] || 'Pending';
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  
  useEffect(() => {
    const fetchCandidates = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${environment.baseUrl}${environment.endpoints.getCandidates}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        const data = await response.json();

        if (!response.ok || data?.response !== true) {
          throw new Error(data?.message || 'Failed to fetch candidates.');
        }

        const candidateData = Array.isArray(data?.data) ? data.data : [];
        setCandidates(candidateData);

        const posts = [...new Set(candidateData.map(c => c.post_name).filter(Boolean))];
        setUniquePosts(posts);
      } catch (err) {
        console.error('Error fetching candidates:', err);
        setError(err.message || 'Unable to load candidates.');
        setCandidates([]);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchMasterData = async () => {
      try {
        const response = await fetch(`${environment.baseUrl}${environment.endpoints.getMasterData || '/api/master-data'}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        const data = await response.json();

        if (data?.response) {
          setUniqueGenders(data.data.genders || []);
          setUniqueCategories(data.data.categories || []);
        } else {
          setUniqueGenders([
            { id: 1, label: 'Male' },
            { id: 2, label: 'Female' },
            { id: 3, label: 'Other' }
          ]);
          setUniqueCategories([
            { id: 1, label: 'General' },
            { id: 2, label: 'OBC' },
            { id: 3, label: 'SC' },
            { id: 4, label: 'ST' },
            { id: 5, label: 'EWS' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching master data:', error);
        setUniqueGenders([
          { id: 1, label: 'Male' },
          { id: 2, label: 'Female' },
          { id: 3, label: 'Other' }
        ]);
        setUniqueCategories([
          { id: 1, label: 'General' },
          { id: 2, label: 'OBC' },
          { id: 3, label: 'SC' },
          { id: 4, label: 'ST' },
          { id: 5, label: 'EWS' }
        ]);
      }
    };

    fetchCandidates();
    fetchMasterData();
  }, []);
  const filteredCandidates = candidates.filter(candidate => {
    if (filterName && !candidate.full_name?.toLowerCase().includes(filterName.toLowerCase())) {
      return false;
    }

    if (filterPost && candidate.post_name !== filterPost) {
      return false;
    }

    if (filterDate) {
      const candidateDate = new Date(candidate.created_at);
      const filterDateObj = new Date(filterDate);

      const sameDate = candidateDate.getFullYear() === filterDateObj.getFullYear() &&
        candidateDate.getMonth() === filterDateObj.getMonth() &&
        candidateDate.getDate() === filterDateObj.getDate();

      if (!sameDate) {
        return false;
      }
    }

    if (filterGender && getGenderLabel(candidate.gender) !== filterGender) {
      return false;
    }

    if (filterCategory && getCategoryLabel(candidate.category) !== filterCategory) {
      return false;
    }

    return true;
  });


  // ✅ Helper Functions for Display

   // ✅ Edit Modal Handlers
const openEditModal = (candidate) => {
  setSelectedCandidate(candidate);
  setEditFormData({
    id: candidate.id,
    full_name: candidate.full_name || '',
    fathers_name: candidate.fathers_name || '',
    email: candidate.email || '',
    contact_no: candidate.contact_no || '',
    date_of_birth: candidate.date_of_birth || '',
    gender: getGenderLabel(candidate.gender),
    marital_status: getMaritalStatusLabel(candidate.marital_status),
    category: getCategoryLabel(candidate.category),
    status: candidate.status || 1,
  });
  setIsEditModalOpen(true);
};

const closeEditModal = () => {
  setIsEditModalOpen(false);
  setSelectedCandidate(null);
  setEditFormData({});
};

// ✅ Edit Form Change Handler
const handleEditChange = (e) => {
  const { name, value } = e.target;
  setEditFormData(prev => ({ ...prev, [name]: value }));
};

// ✅ Edit Form Submit
const handleEditSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const payload = {
      candidate_name: editFormData.full_name,
      fathers_name: editFormData.fathers_name,
      email: editFormData.email,
      contact_no: editFormData.contact_no,
      date_of_birth: editFormData.date_of_birth,
      gender: getGenderValue(editFormData.gender) || 1,
      marital_status: getMaritalStatusValue(editFormData.marital_status) || 1,
      category: getCategoryValue(editFormData.category) || 1,
      status: editFormData.status || 1,
    };

    const response = await fetch(`${environment.baseUrl}candidates/update/${editFormData.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok || data?.response !== true) {
      throw new Error(data?.message || 'Failed to update candidate.');
    }

    await Swal.fire({
      icon: 'success',
      title: 'Updated!',
      text: 'Candidate details updated successfully.',
      confirmButtonColor: '#0f7a8f',
    });

    // ✅ Refresh data
    const refreshResponse = await fetch(`${environment.baseUrl}${environment.endpoints.getCandidates}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    const refreshData = await refreshResponse.json();
    if (refreshData?.response === true) {
      setCandidates(Array.isArray(refreshData?.data) ? refreshData.data : []);
    }

    closeEditModal();
  } catch (err) {
    console.error('Error updating candidate:', err);
    await Swal.fire({
      icon: 'error',
      title: 'Update Failed',
      text: err.message || 'Unable to update candidate.',
      confirmButtonColor: '#c7374e',
    });
  } finally {
    setIsSubmitting(false);
  }
};

// ✅ Add these helper functions (near other helper functions)
const getGenderValue = (gender) => {
  const genderMap = { 'Male': 1, 'Female': 2, 'Other': 3 };
  return genderMap[gender] || '';
};

const getMaritalStatusValue = (status) => {
  const statusMap = { 'Single': 1, 'Married': 2, 'Divorced': 3, 'Widowed': 4 };
  return statusMap[status] || '';
};

const getCategoryValue = (category) => {
  const categoryMap = { 'General': 1, 'OBC': 2, 'SC': 3, 'ST': 4, 'EWS': 5 };
  return categoryMap[category] || '';
};

  const getMaritalStatusLabel = (status) => {
    const statusMap = {
      1: 'Single',
      2: 'Married',
      3: 'Divorced',
      4: 'Widowed'
    };
    return statusMap[status] || 'N/A';
  };



  // ✅ Full URL helper for images/documents
  const getFullUrl = (path) => {
    if (!path || path === 'N/A') return null;

    const cleanPath = path.replace(/^\//, '');

    const finalPath = cleanPath.startsWith('api/')
      ? cleanPath.replace('api/', '')
      : cleanPath;

    const baseUrl = environment.baseUrl.endsWith('/')
      ? environment.baseUrl
      : `${environment.baseUrl}/`;

    return `${baseUrl}${finalPath}`;
  };

  // ✅ Modal Handlers
  const openCandidateModal = (candidate) => {
    setSelectedCandidate(candidate);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCandidate(null);
  };

  // ✅ Loading State
  if (isLoading) {
    return (
      <DashboardLayout
        displayName="Admin"
        pageTitle="Candidates"
        dashboard={{
          active: false,
          onClick: () => navigate('/welcome'),
        }}
        sections={sections}
        onLogout={() => { }}
      >
        <div className="candidate-loading">
          <div className="candidate-spinner"></div>
          <p>Loading candidates...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        displayName="Admin"
        pageTitle="Candidates"
        dashboard={{
          active: false,
          onClick: () => navigate('/welcome'),
        }}
        sections={sections}
        onLogout={() => { }}
      >
        <div className="candidate-error">
          <span>⚠️</span>
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="candidate-retry-btn">
            Try Again
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      displayName="Admin"
      pageTitle="Candidates"
      dashboard={{
        active: false,
        onClick: () => navigate('/welcome'),
      }}
      sections={sections}
      onLogout={() => { }}
    >
      <div className="candidate-page">
        <div className="candidate-filter-bar">
          <div className="candidate-filter-group">
            <input
              type="text"
              placeholder="Filter by name..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="candidate-filter-input"
            />
          </div>

          <div className="candidate-filter-group">
            <select
              value={filterPost}
              onChange={(e) => setFilterPost(e.target.value)}
              className="candidate-filter-select"
            >
              <option value="">All Posts</option>
              {uniquePosts.map(post => (
                <option key={post} value={post}>{post}</option>
              ))}
            </select>
          </div>

          <div className="candidate-filter-group">
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="candidate-filter-input"
            />
          </div>

          <div className="candidate-filter-group">
            <select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              className="candidate-filter-select"
            >
              <option value="">All Genders</option>
              {uniqueGenders.map(gender => (
                <option key={gender.id} value={gender.label}>{gender.label}</option>
              ))}
            </select>
          </div>

          <div className="candidate-filter-group">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="candidate-filter-select"
            >
              <option value="">All Categories</option>
              {uniqueCategories.map(category => (
                <option key={category.id} value={category.label}>{category.label}</option>
              ))}
            </select>
          </div>

          <button
            className="candidate-clear-filters"
            onClick={() => {
              setFilterName('');
              setFilterPost('');
              setFilterDate('');
              setFilterGender('');
              setFilterCategory('');
            }}
          >
            ✕ Clear
          </button>

          <span className="candidate-total">
            Total: {filteredCandidates.length} Candidates
          </span>
        </div>

        <div className="candidate-table-wrapper">
          {filteredCandidates.length > 0 ? (
            <table className="candidate-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '60px' }}>S.No</th>
                  <th style={{ minWidth: '180px' }}>Name</th>
                  <th style={{ minWidth: '180px' }}>Father's Name</th>
                  <th style={{ minWidth: '220px' }}>Email</th>
                  <th style={{ minWidth: '130px' }}>Mobile</th>
                  <th style={{ minWidth: '150px' }}>Post Applied</th>
                  <th style={{ minWidth: '120px' }}>DOB</th>
                  <th style={{ minWidth: '100px' }}>Gender</th>
                  <th style={{ minWidth: '120px' }}>Category</th>
                  <th style={{ minWidth: '130px' }}>Status</th>
                  <th style={{ minWidth: '140px' }}>Applied On</th>
                  <th style={{ minWidth: '140px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.map((candidate, index) => (
                  <tr key={candidate.id}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="candidate-name-cell">
                        <div className="candidate-avatar">
                          {candidate.full_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        {candidate.full_name || 'N/A'}
                      </div>
                    </td>
                    <td>{candidate.fathers_name || 'N/A'}</td>
                    <td>{candidate.email || 'N/A'}</td>
                    <td>{candidate.contact_no || 'N/A'}</td>
                    <td>{candidate.post_name || 'N/A'}</td>
                    <td>{formatDate(candidate.date_of_birth)}</td>
                    <td>{getGenderLabel(candidate.gender)}</td>
                    <td>{getCategoryLabel(candidate.category)}</td>
                    <td>
                      <span className={`candidate-status ${candidate.status === 1 ? 'active' : 'inactive'}`}>
                        {getStatusLabel(candidate.status)}
                      </span>
                    </td>
                    <td>{formatDate(candidate.created_at)}</td>
                    <td className="candidate-action-cell">
                      {/* ✅ View Button - Eye Icon */}
                      <button
                        className="candidate-action-btn view-btn"
                        onClick={() => openCandidateModal(candidate)}
                        title="View Details"
                      >
                        <svg
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>

                      <button
                        className="candidate-action-btn edit-btn"
                        onClick={() => openEditModal(candidate)}
                        title="Edit Candidate"
                      >
                        <svg
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="candidate-empty">
              <span>📋</span>
              <h3>No candidates found</h3>
              <p>No applications submitted yet.</p>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && selectedCandidate && (
        <div className="candidate-modal-overlay" onClick={closeModal}>
          <div className="candidate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="candidate-modal-header">
              <h3>Candidate Details</h3>
              <button className="candidate-modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="candidate-modal-body">
              <div className="candidate-profile-section">
                {getFullUrl(selectedCandidate.profile_image) ? (
                  <img
                    src={getFullUrl(selectedCandidate.profile_image)}
                    alt="Profile"
                    className="candidate-profile-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="candidate-profile-placeholder" style={{ display: getFullUrl(selectedCandidate.profile_image) ? 'none' : 'flex' }}>
                  <span>👤</span>
                </div>
                <div className="candidate-profile-info">
                  <h4>{selectedCandidate.full_name || 'N/A'}</h4>
                  <p><strong>Post Applied:</strong> {selectedCandidate.post_name || 'N/A'}</p>
                  <p><strong>Status:</strong> {getStatusLabel(selectedCandidate.status)}</p>
                  <p><strong>Applied On:</strong> {formatDate(selectedCandidate.created_at)}</p>
                </div>
              </div>

              <div className="candidate-details-grid">
                <div className="candidate-detail-item">
                  <span className="detail-label">Full Name</span>
                  <span className="detail-value">{selectedCandidate.full_name || 'N/A'}</span>
                </div>
                <div className="candidate-detail-item">
                  <span className="detail-label">Father's Name</span>
                  <span className="detail-value">{selectedCandidate.fathers_name || 'N/A'}</span>
                </div>
                <div className="candidate-detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{selectedCandidate.email || 'N/A'}</span>
                </div>
                <div className="candidate-detail-item">
                  <span className="detail-label">Contact Number</span>
                  <span className="detail-value">{selectedCandidate.contact_no || 'N/A'}</span>
                </div>
                <div className="candidate-detail-item">
                  <span className="detail-label">Date of Birth</span>
                  <span className="detail-value">{formatDate(selectedCandidate.date_of_birth)}</span>
                </div>
                <div className="candidate-detail-item">
                  <span className="detail-label">Gender</span>
                  <span className="detail-value">{getGenderLabel(selectedCandidate.gender)}</span>
                </div>
                <div className="candidate-detail-item">
                  <span className="detail-label">Marital Status</span>
                  <span className="detail-value">{getMaritalStatusLabel(selectedCandidate.marital_status)}</span>
                </div>
                <div className="candidate-detail-item">
                  <span className="detail-label">Category</span>
                  <span className="detail-value">{getCategoryLabel(selectedCandidate.category)}</span>
                </div>
                {selectedCandidate.latitude && selectedCandidate.latitude !== 'N/A' && (
                  <div className="candidate-detail-item">
                    <span className="detail-label">Location</span>
                    <span className="detail-value">
                      {selectedCandidate.latitude}, {selectedCandidate.longitude}
                    </span>
                  </div>
                )}
              </div>

              {/* ✅ Documents Section */}
              <div className="candidate-documents-section">
                <h4>Documents</h4>
                <div className="candidate-documents-grid">

                  {/* Resume */}
                  {selectedCandidate.resume && selectedCandidate.resume !== 'N/A' ? (
                    <div className="candidate-document-item">
                      <span className="document-icon">📄</span>
                      <span className="document-name">Resume</span>
                      <a
                        href={getFullUrl(selectedCandidate.resume)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="document-link"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(getFullUrl(selectedCandidate.resume), '_blank');
                        }}
                      >
                        View
                      </a>
                    </div>
                  ) : (
                    <div className="candidate-document-item">
                      <span className="document-icon">📄</span>
                      <span className="document-name">Resume</span>
                      <span className="document-na">Not Available</span>
                    </div>
                  )}

                  {/* Other Document 1 */}
                  {selectedCandidate.other_document_1 && selectedCandidate.other_document_1 !== 'N/A' ? (
                    <div className="candidate-document-item">
                      <span className="document-icon">📄</span>
                      <span className="document-name">Document 1</span>
                      <a
                        href={getFullUrl(selectedCandidate.other_document_1)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="document-link"
                      >
                        View
                      </a>
                    </div>
                  ) : (
                    <div className="candidate-document-item">
                      <span className="document-icon">📄</span>
                      <span className="document-name">Document 1</span>
                      <span className="document-na">Not Available</span>
                    </div>
                  )}

                  {/* Other Document 2 */}
                  {selectedCandidate.other_document_2 && selectedCandidate.other_document_2 !== 'N/A' ? (
                    <div className="candidate-document-item">
                      <span className="document-icon">📄</span>
                      <span className="document-name">Document 2</span>
                      <a
                        href={getFullUrl(selectedCandidate.other_document_2)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="document-link"
                      >
                        View
                      </a>
                    </div>
                  ) : (
                    <div className="candidate-document-item">
                      <span className="document-icon">📄</span>
                      <span className="document-name">Document 2</span>
                      <span className="document-na">Not Available</span>
                    </div>
                  )}
                </div>
              </div>

              {/* ✅ ID Info */}
              <div className="candidate-id-info">
                <p><strong>Candidate ID:</strong> {selectedCandidate.id}</p>
                <p><strong>Post ID:</strong> {selectedCandidate.post_id}</p>
                <p><strong>Status:</strong> {getStatusLabel(selectedCandidate.status)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ✅ Edit Modal */}
{isEditModalOpen && selectedCandidate && (
  <div className="candidate-modal-overlay" onClick={closeEditModal}>
    <div className="candidate-modal candidate-edit-modal" onClick={(e) => e.stopPropagation()}>
      <div className="candidate-modal-header">
        <h3>Edit Candidate</h3>
        <button className="candidate-modal-close" onClick={closeEditModal}>✕</button>
      </div>
      <form className="candidate-modal-body" onSubmit={handleEditSubmit}>
        <div className="edit-form-grid">
          <div className="edit-form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="full_name"
              value={editFormData.full_name || ''}
              onChange={handleEditChange}
              required
            />
          </div>
          <div className="edit-form-group">
            <label>Father's Name *</label>
            <input
              type="text"
              name="fathers_name"
              value={editFormData.fathers_name || ''}
              onChange={handleEditChange}
              required
            />
          </div>
          <div className="edit-form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={editFormData.email || ''}
              onChange={handleEditChange}
              required
            />
          </div>
          <div className="edit-form-group">
            <label>Contact Number *</label>
            <input
              type="text"
              name="contact_no"
              value={editFormData.contact_no || ''}
              onChange={handleEditChange}
              required
            />
          </div>
          <div className="edit-form-group">
            <label>Date of Birth</label>
            <input
              type="date"
              name="date_of_birth"
              value={editFormData.date_of_birth || ''}
              onChange={handleEditChange}
            />
          </div>
          <div className="edit-form-group">
            <label>Gender</label>
            <select name="gender" value={editFormData.gender || ''} onChange={handleEditChange}>
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="edit-form-group">
            <label>Marital Status</label>
            <select name="marital_status" value={editFormData.marital_status || ''} onChange={handleEditChange}>
              <option value="">Select</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
            </select>
          </div>
          <div className="edit-form-group">
            <label>Category</label>
            <select name="category" value={editFormData.category || ''} onChange={handleEditChange}>
              <option value="">Select</option>
              <option value="General">General</option>
              <option value="OBC">OBC</option>
              <option value="SC">SC</option>
              <option value="ST">ST</option>
              <option value="EWS">EWS</option>
            </select>
          </div>
          <div className="edit-form-group">
            <label>Status</label>
            <select name="status" value={editFormData.status || 1} onChange={handleEditChange}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
              <option value={2}>Under Review</option>
              <option value={3}>Shortlisted</option>
              <option value={4}>Rejected</option>
            </select>
          </div>
        </div>

        <div className="edit-form-actions">
          <button type="button" className="edit-cancel-btn" onClick={closeEditModal}>
            Cancel
          </button>
          <button type="submit" className="edit-save-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  </div>
)}
    </DashboardLayout>
  );
};

export default CandidateList;